import React from "react";
import { ArrowUpRight } from "lucide-react";
import type { SocialQueueItem, DataRecord } from "../../../types";
import { stripGoogleTranslation } from "../socialFormat";
import { getClientBrandMeta } from "../socialBrand";

interface Props {
  clientSlug: string;
  activePlatform: string;
  criticalItems: SocialQueueItem[];
  onOpenReplyModal: (item: SocialQueueItem) => void;
  onSelectCommentForModal: (item: SocialQueueItem | DataRecord) => void;
  getDirectLink: (row: any) => string;
  renderStars: (rating: number, color?: string) => React.ReactNode;
  renderPlatformBadge: () => React.ReactNode;
}

export function SocialActionImmediateSection({
  clientSlug,
  activePlatform,
  criticalItems,
  onOpenReplyModal,
  onSelectCommentForModal,
  getDirectLink,
  renderStars,
  renderPlatformBadge,
}: Props) {
  const brandMeta = getClientBrandMeta(clientSlug);
  const text = (v: any, fallback = "") => (v !== null && v !== undefined ? String(v) : fallback);

  const isMetaIg = activePlatform === "meta_ig" || activePlatform === "instagram";
  const isMetaFb = activePlatform === "meta_fb" || activePlatform === "facebook";
  const isGoogle = activePlatform === "gbp" || activePlatform === "google";

  const platformBadgeLabel = isMetaIg
    ? "Instagram"
    : isMetaFb
    ? "Facebook"
    : isGoogle
    ? "Google BP"
    : "Visión Global";

  return (
    <section id="section-social-immediate" className="social-editorial-section">
      <div className="social-section-tag">ACCIÓN INMEDIATA</div>
      <h2 className="social-section-title">
        {isMetaIg
          ? "Responde primero los comentarios críticos de Instagram"
          : isMetaFb
          ? "Responde primero los comentarios críticos de Facebook"
          : isGoogle
          ? "Responde primero las reseñas críticas de Google"
          : "Responde primero los comentarios y reseñas críticas"}
      </h2>
      <p className="social-section-subtitle">
        {isGoogle
          ? "Reseñas negativas de urgencia alta que siguen sin respuesta. Al pulsar responder, se abre el borrador asistido para revisar y aprobar."
          : `Comentarios negativos de urgencia alta en ${platformBadgeLabel} que siguen sin respuesta, con borrador listo al hacer clic en responder.`}
      </p>

      <div className="social-editorial-badges">
        {renderPlatformBadge()}
        <span className="social-role-badge">Comunicaciones</span>
      </div>

      {criticalItems.length === 0 ? (
        <div style={{ padding: "24px", textAlign: "center", background: "var(--surface, #11141b)", borderRadius: "12px", color: "var(--green, #2ed573)", fontSize: "13px" }}>
          {isGoogle
            ? "✓ No hay alertas urgentes ni reseñas críticas pendientes en Google."
            : isMetaIg
            ? "✓ No hay comentarios críticos pendientes en Instagram."
            : isMetaFb
            ? "✓ No hay comentarios críticos pendientes en Facebook."
            : "✓ No hay alertas urgentes ni comentarios críticos pendientes en esta ventana."}
        </div>
      ) : (
        <div className="social-cards-grid-4">
          {criticalItems.map((item) => {
            const defaultPlat = isGoogle ? "gbp" : isMetaIg ? "meta_ig" : isMetaFb ? "meta_fb" : "meta_ig";
            const itemPlat = text(item.platform, defaultPlat).toLowerCase();
            const isGbpItem = itemPlat === "gbp" || itemPlat.includes("google");
            const isPaid = text(item.origin).toLowerCase() === "paid";
            const rawMessage = text(item.message);
            const cleanedMessage = stripGoogleTranslation(rawMessage);
            const isLong = cleanedMessage.length > 175;

            return (
              <div key={item.feedback_id} className="social-editorial-card border-negative">
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px", gap: "8px" }}>
                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px" }}>
                      <span
                        style={{
                          fontSize: "11px",
                          fontWeight: 750,
                          padding: "2px 7px",
                          borderRadius: "4px",
                          background: isGbpItem
                            ? "rgba(52, 168, 83, 0.15)"
                            : itemPlat.includes("ig")
                            ? "rgba(225, 48, 108, 0.15)"
                            : "rgba(24, 119, 242, 0.15)",
                          color: isGbpItem ? "#34a853" : itemPlat.includes("ig") ? "#e1306c" : "#1877f2",
                        }}
                      >
                        {isGbpItem ? "Google BP" : itemPlat.includes("ig") ? "Instagram" : "Facebook"}
                      </span>

                      <span
                        style={{
                          fontSize: "10.5px",
                          fontWeight: 650,
                          padding: "2px 6px",
                          borderRadius: "4px",
                          background: isPaid ? "rgba(255, 159, 67, 0.15)" : "var(--surface-2, rgba(255, 255, 255, 0.06))",
                          color: isPaid ? "var(--orange, #ff9f43)" : "var(--muted, #8e96a8)",
                          border: "1px solid var(--line, rgba(255, 255, 255, 0.08))",
                        }}
                      >
                        {isPaid ? "Anuncio (Paid)" : "Orgánico"}
                      </span>

                      {isGbpItem && item.star_rating !== null && renderStars(item.star_rating, "#ff5c5c")}

                      <span style={{ fontSize: "11px", color: "var(--muted, #8e96a8)" }}>
                        {item.page_id || "Canal principal"}
                      </span>
                    </div>
                    <span style={{ fontSize: "11px", color: "var(--red, #ff5c5c)", fontWeight: 650, whiteSpace: "nowrap", flexShrink: 0 }}>
                      Hace {item.date ? Math.max(1, Math.round((Date.now() - new Date(item.date).getTime()) / (1000 * 60 * 60 * 24))) : 7} d
                    </span>
                  </div>

                  <p style={{ margin: "4px 0 6px 0", fontSize: "12.5px", color: "var(--text, #f6f7fb)", lineHeight: 1.45 }}>
                    "{isLong ? `${cleanedMessage.slice(0, 175)}…` : cleanedMessage}"
                  </p>

                  {isLong && (
                    <button
                      type="button"
                      onClick={() => onSelectCommentForModal(item)}
                      style={{
                        background: "transparent",
                        border: "none",
                        color: "var(--blue, #7d96ff)",
                        fontSize: "11.5px",
                        cursor: "pointer",
                        padding: "0 0 6px 0",
                        fontWeight: 600,
                        textDecoration: "underline",
                      }}
                    >
                      Ver comentario completo
                    </button>
                  )}

                  <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center", marginTop: "4px" }}>
                    <span className="social-badge-unanswered">
                      Sin responder
                    </span>
                  </div>
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: "10px", borderTop: "1px solid var(--line, rgba(255, 255, 255, 0.06))", marginTop: "8px" }}>
                  <a
                    href={getDirectLink(item)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: "11px", color: "var(--muted, #8e96a8)", display: "inline-flex", alignItems: "center", gap: "3px", textDecoration: "none" }}
                  >
                    {isGbpItem ? "Ver en Maps" : itemPlat.includes("ig") ? "Ver en Instagram" : itemPlat.includes("fb") ? "Ver en Facebook" : "Ver publicación"} <ArrowUpRight size={11} />
                  </a>

                  <button
                    type="button"
                    onClick={() => onOpenReplyModal(item)}
                    style={{
                      background: "transparent",
                      border: "none",
                      color: isGbpItem ? "#34a853" : "var(--blue, #7d96ff)",
                      fontSize: "11.5px",
                      fontWeight: 650,
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                      padding: 0,
                    }}
                  >
                    Responder
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
